// /frontend/app/src/components/ui/sonner.tsx - Enterprise-grade toast component with enhanced styling

import { Toaster as Sonner } from "sonner"
import { cn } from "@/lib/utils"

type ToasterProps = React.ComponentProps<typeof Sonner>

const Toaster = ({ className, ...props }: ToasterProps) => {
  return (
    <Sonner
      className={cn(
        "toaster group",
        "pointer-events-none z-[100] fixed top-4 right-4 max-w-md",
        className
      )}
      toastOptions={{
        duration: 5000, // 5 seconds for all toasts
        closeButton: true, // Add close button for manual dismissal
        classNames: {
          toast: cn(
            "pointer-events-auto group toast relative flex items-center w-full min-h-[4rem] p-4 rounded-lg border-0 shadow-lg backdrop-blur-sm",
            // Success toast - Dark green background
            "[&[data-type=success]]:bg-green-600 [&[data-type=success]]:text-white [&[data-type=success]]:shadow-green-600/25",
            // Error toast - Red background  
            "[&[data-type=error]]:bg-red-600 [&[data-type=error]]:text-white [&[data-type=error]]:shadow-red-600/25",
            // Warning toast - Orange background
            "[&[data-type=warning]]:bg-orange-600 [&[data-type=warning]]:text-white [&[data-type=warning]]:shadow-orange-600/25",
            // Info toast - Gray background
            "[&[data-type=default]]:bg-gray-600 [&[data-type=default]]:text-white [&[data-type=default]]:shadow-gray-600/25",
            // Loading toast - Blue background
            "[&[data-type=loading]]:bg-blue-600 [&[data-type=loading]]:text-white [&[data-type=loading]]:shadow-blue-600/25"
          ),
          icon: cn(
            "flex-shrink-0 w-6 h-6 mr-3",
            // Icon colors based on toast type
            "[&[data-type=success]]:text-green-100",
            "[&[data-type=error]]:text-red-100", 
            "[&[data-type=warning]]:text-orange-100",
            "[&[data-type=default]]:text-gray-100",
            "[&[data-type=loading]]:text-blue-100"
          ),
          description: "group-[.toast]:text-white/90 font-medium",
          actionButton: 
            "group-[.toast]:bg-white/20 group-[.toast]:text-white hover:group-[.toast]:bg-white/30 backdrop-blur-sm",
          cancelButton:
            "group-[.toast]:bg-white/20 group-[.toast]:text-white hover:group-[.toast]:bg-white/30 backdrop-blur-sm",
          closeButton: 
            "group-[.toast]:text-white/80 hover:group-[.toast]:text-white hover:group-[.toast]:bg-white/20 transition-all duration-200 p-2 rounded-md",
        },
      }}
      {...props}
    />
  )
}

export { Toaster }
